/**
 * Countries and the spellings that resolve to them (f2.2).
 *
 * A country is not a lookup type: it carries aliases, and the screen shows
 * them because they are the whole reason the table exists. The legacy
 * workbooks spell the same country several ways -- `SWI` for Switzerland,
 * `Belgique` for Belgium -- and every variant is an explicit row, never a
 * similarity score.
 *
 * The alias set is edited in ONE control, as text, because the set is what
 * the server replaces: there is no alias id to address a single spelling by,
 * and a spelling left out is a spelling retired.
 */

import { useMemo, useState } from 'react';
import { Globe2, Lock, Pencil, Plus } from 'lucide-react';

import { ApiError, type Country } from '@/api/client';
import { useAddCountry, useUpdateCountry } from '@/api/queries';
import { EmptyCell } from '@/ui/EmptyCell';
import { SortableHeader } from '@/ui/SortableHeader';
import { nextSort, sortRows, type SortState } from '@/ui/tableSort';

import { DialogField, Modal } from './Modal';
import { formatAliases, parseAliases, problemsByKey } from './referenceForm';
import {
  COUNTRY_DEFAULT_SORT,
  countrySortValue,
  type CountrySortColumn,
} from './referenceSort';

const CODE_IMMUTABLE = 'A country code is resolved by imports. It cannot be changed once it exists.';

const ALIAS_HINT =
  'One spelling per line. This is the FULL set; a spelling removed here stops resolving.';

export function CountriesPanel({ countries }: { countries: readonly Country[] }) {
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<Country | null>(null);
  const [sort, setSort] = useState<SortState<CountrySortColumn>>(COUNTRY_DEFAULT_SORT);

  const ordered = useMemo(
    () => sortRows(countries, sort, countrySortValue),
    [countries, sort],
  );
  const toggleSort = (column: CountrySortColumn) =>
    setSort((current) => nextSort(current, column));

  return (
    <section className="card ref-card ref-panel">
      <div className="card-head">
        <div>
          <h3 className="ref-card-title">Countries</h3>
          <span className="hint">
            The countries an import can resolve, each with every spelling that resolves to it.
          </span>
        </div>
        <div className="ref-head-actions">
          <button type="button" className="btn btn-primary btn-sm" onClick={() => setAdding(true)}>
            <Plus aria-hidden="true" /> Add country
          </button>
        </div>
      </div>
      <div className="card-body">
        {countries.length === 0 ? (
          <div className="empty">
            <div className="e-icon">
              <Globe2 aria-hidden="true" />
            </div>
            <h3>No countries yet</h3>
            <p>Add a country to let an import resolve one.</p>
          </div>
        ) : (
          <div className="table-scroll">
            <table className="data ref-table">
              <thead>
                {/* Every column sorts except the last, which holds the row
                    action and has nothing to order by. */}
                <tr>
                  <SortableHeader column="code" label="Code" sort={sort} onSort={toggleSort} />
                  <SortableHeader column="name" label="Name" sort={sort} onSort={toggleSort} />
                  <SortableHeader
                    column="aliases"
                    label="Also resolves from"
                    sort={sort}
                    onSort={toggleSort}
                  />
                  <SortableHeader
                    column="active"
                    label="Active"
                    sort={sort}
                    onSort={toggleSort}
                  />
                  <th scope="col">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {ordered.map((country) => (
                  <tr key={country.id}>
                    <td>
                      <span className="mono ref-code" title={CODE_IMMUTABLE}>
                        <Lock aria-hidden="true" />
                        {country.code}
                      </span>
                    </td>
                    <td>{country.name}</td>
                    <td>
                      {country.aliases.length === 0 ? (
                        <EmptyCell />
                      ) : (
                        <span className="ref-chips">
                          {country.aliases.map((alias) => (
                            <span key={alias} className="badge badge-outline" title={alias}>
                              {alias}
                            </span>
                          ))}
                        </span>
                      )}
                    </td>
                    <td>
                      {country.is_active ? (
                        <span className="badge pill-success">Active</span>
                      ) : (
                        <span className="badge badge-muted">Inactive</span>
                      )}
                    </td>
                    <td>
                      <div className="row-actions">
                        <button
                          type="button"
                          className="btn btn-secondary btn-icon"
                          title="Edit country"
                          aria-label={`Edit ${country.name}`}
                          onClick={() => setEditing(country)}
                        >
                          <Pencil aria-hidden="true" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {adding && <AddCountryDialog onClose={() => setAdding(false)} />}
      {editing && <EditCountryDialog country={editing} onClose={() => setEditing(null)} />}
    </section>
  );
}

function AddCountryDialog({ onClose }: { onClose: () => void }) {
  const add = useAddCountry();
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [aliases, setAliases] = useState('');
  const [problems, setProblems] = useState<Record<string, string>>({});
  const [message, setMessage] = useState<string | null>(null);

  const submit = () => {
    if (add.isPending) return;
    setProblems({});
    setMessage(null);
    add.mutate(
      { code, name, aliases: parseAliases(aliases) },
      {
        onSuccess: onClose,
        onError: (error) => {
          if (error instanceof ApiError && error.status < 500) {
            setProblems(problemsByKey(error.errors));
            setMessage(error.errors.some((item) => item.key) ? null : error.message);
            return;
          }
          setMessage(error instanceof Error ? error.message : 'The country could not be added.');
        },
      },
    );
  };

  return (
    <Modal
      title="Add a country"
      subtitle="Every spelling must resolve to exactly one country."
      onClose={onClose}
      onSubmit={submit}
      busy={add.isPending}
      submitLabel={add.isPending ? 'Adding…' : 'Add country'}
      message={message}
    >
      <DialogField
        label="Code"
        htmlFor="country-code"
        problem={problems.code}
        hint="Two to four letters. It cannot be changed later."
      >
        <input
          id="country-code"
          type="text"
          className="mono"
          value={code}
          onChange={(event) => setCode(event.target.value)}
          disabled={add.isPending}
        />
      </DialogField>
      <DialogField label="Name" htmlFor="country-name" problem={problems.name}>
        <input
          id="country-name"
          type="text"
          value={name}
          onChange={(event) => setName(event.target.value)}
          disabled={add.isPending}
        />
      </DialogField>
      <DialogField
        label="Also resolves from"
        htmlFor="country-aliases"
        problem={problems.aliases}
        hint={ALIAS_HINT}
        span
      >
        <textarea
          id="country-aliases"
          rows={3}
          value={aliases}
          onChange={(event) => setAliases(event.target.value)}
          disabled={add.isPending}
        />
      </DialogField>
    </Modal>
  );
}

function EditCountryDialog({ country, onClose }: { country: Country; onClose: () => void }) {
  const update = useUpdateCountry();
  const [name, setName] = useState(country.name);
  const [aliases, setAliases] = useState(formatAliases(country.aliases));
  const [isActive, setIsActive] = useState(country.is_active);
  const [problems, setProblems] = useState<Record<string, string>>({});
  const [message, setMessage] = useState<string | null>(null);

  const submit = () => {
    if (update.isPending) return;
    setProblems({});
    setMessage(null);
    update.mutate(
      {
        countryId: country.id,
        // The full alias set every time: it REPLACES the stored one.
        change: { name, is_active: isActive, aliases: parseAliases(aliases) },
      },
      {
        onSuccess: onClose,
        onError: (error) => {
          if (error instanceof ApiError && error.status < 500) {
            setProblems(problemsByKey(error.errors));
            setMessage(error.errors.some((item) => item.key) ? null : error.message);
            return;
          }
          setMessage(error instanceof Error ? error.message : 'The country could not be saved.');
        },
      },
    );
  };

  return (
    <Modal
      title={`Edit ${country.name}`}
      subtitle={`Code ${country.code} cannot be changed.`}
      onClose={onClose}
      onSubmit={submit}
      busy={update.isPending}
      submitLabel={update.isPending ? 'Saving…' : 'Save country'}
      message={message}
    >
      <DialogField label="Name" htmlFor="edit-country-name" problem={problems.name}>
        <input
          id="edit-country-name"
          type="text"
          value={name}
          onChange={(event) => setName(event.target.value)}
          disabled={update.isPending}
        />
      </DialogField>
      <DialogField label="Active" htmlFor="edit-country-active" problem={problems.is_active}>
        <label className="check">
          <input
            id="edit-country-active"
            type="checkbox"
            checked={isActive}
            onChange={(event) => setIsActive(event.target.checked)}
            disabled={update.isPending}
          />
          Selectable for new records
        </label>
      </DialogField>
      <DialogField
        label="Also resolves from"
        htmlFor="edit-country-aliases"
        problem={problems.aliases}
        hint={ALIAS_HINT}
        span
      >
        <textarea
          id="edit-country-aliases"
          rows={4}
          value={aliases}
          onChange={(event) => setAliases(event.target.value)}
          disabled={update.isPending}
        />
      </DialogField>
    </Modal>
  );
}
